// src/components/CircuitManager.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Select, MenuItem, Typography, Card, CardContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getCircuits, addCircuit, updateCircuit, deleteCircuit, isCircuitUsed } from '../utils/firestoreUtils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase';
import Compressor from 'compressorjs';
import 'flag-icons/css/flag-icons.min.css';

function CircuitManager() {
  const navigate = useNavigate();
  const [circuits, setCircuits] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCircuit, setEditingCircuit] = useState(null);
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [layouts, setLayouts] = useState([]);
  const [layoutName, setLayoutName] = useState('');
  const [lengthKm, setLengthKm] = useState('');
  const [lengthMiles, setLengthMiles] = useState('');
  const [mapFile, setMapFile] = useState(null);
  const [unsaved, setUnsaved] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [editingLayoutIndex, setEditingLayoutIndex] = useState(null);
  const [showConfirmDeleteLayout, setShowConfirmDeleteLayout] = useState(false);
  const [layoutToDelete, setLayoutToDelete] = useState(null);
  const [canDeleteCircuit, setCanDeleteCircuit] = useState(false);
  const [showConfirmDeleteCircuit, setShowConfirmDeleteCircuit] = useState(false);

  useEffect(() => {
    const fetchCircuits = async () => {
      const circs = await getCircuits();
      setCircuits(circs);
    };
    fetchCircuits();
  }, []);

  const handleAddCircuit = async () => {
    if (isEditing) {
      await updateCircuit(editingCircuit.id, { name, country, layouts });
    } else {
      await addCircuit({ name, country, layouts });
    }
    setOpenAddModal(false);
    setIsEditing(false);
    setEditingCircuit(null);
    resetForm();
    // Refresh circuits
    const circs = await getCircuits();
    setCircuits(circs);
  };

  const resetForm = () => {
    setName('');
    setCountry('');
    setLayouts([]);
    setLayoutName('');
    setLengthKm('');
    setLengthMiles('');
    setMapFile(null);
    setUnsaved(false);
    setEditingLayoutIndex(null);
    setShowConfirmDeleteLayout(false);
    setLayoutToDelete(null);
    setCanDeleteCircuit(false);
    setShowConfirmDeleteCircuit(false);
  };

  const handleEditCircuit = async (circuit) => {
    setIsEditing(true);
    setEditingCircuit(circuit);
    setName(circuit.name);
    setCountry(circuit.country);
    setLayouts(circuit.layouts || []);
    setOpenAddModal(true);
    setUnsaved(false);
    setEditingLayoutIndex(null);
    const used = await isCircuitUsed(circuit.id);
    setCanDeleteCircuit(!used);
  };

  const handleClose = () => {
    if (unsaved) {
      setShowConfirmClose(true);
    } else {
      setOpenAddModal(false);
      setIsEditing(false);
      resetForm();
    }
  };

  const handleDeleteCircuit = () => {
    setShowConfirmDeleteCircuit(true);
  };

  const confirmDeleteCircuit = async () => {
    if (editingCircuit) {
      await deleteCircuit(editingCircuit.id);
      setShowConfirmDeleteCircuit(false);
      setOpenAddModal(false);
      setIsEditing(false);
      resetForm();
      // Refresh circuits
      const circs = await getCircuits();
      setCircuits(circs);
    }
  };

  const handleAddLayout = async () => {
    let mapUrl = '';
    if (mapFile) {
      const compressedMap = await new Promise((resolve) => {
        new Compressor(mapFile, {
          quality: 0.8,
          maxWidth: 1600,
          success: resolve,
        });
      });
      const mapRef = ref(storage, `circuits/${Date.now()}_map.jpg`);
      await uploadBytes(mapRef, compressedMap);
      mapUrl = await getDownloadURL(mapRef);
    }
    setLayouts([...layouts, { layoutName, length: parseFloat(lengthKm).toFixed(3), map: mapUrl }]);
    setLayoutName('');
    setLengthKm('');
    setLengthMiles('');
    setMapFile(null);
  };

  return (
    <Dialog open={true} fullWidth maxWidth="md">
      <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Circuit Manager
        <IconButton onClick={() => navigate('/admin')}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Button onClick={() => setOpenAddModal(true)} style={{ marginBottom: '24px' }}>Add Circuit</Button>
        <Table className="leaderboard-table">
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Length (km)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {circuits.map((circ) => (
              <TableRow key={circ.id} onClick={() => handleEditCircuit(circ)} style={{ cursor: 'pointer' }}>
                <TableCell><span className={`fi fi-${circ.country.toLowerCase()}`}></span></TableCell>
                <TableCell>{circ.name}</TableCell>
                <TableCell>{circ.layouts[0]?.length || 'N/A'}</TableCell> {/* Simplify for first layout */}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={openAddModal} onClose={handleClose}>
          <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {isEditing ? 'Edit Circuit' : 'Add Circuit'}
            <IconButton onClick={handleClose}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Circuit Name" value={name} onChange={(e) => { setName(e.target.value); setUnsaved(true); }} disabled={isEditing} />
            <Select fullWidth label="Country" value={country} onChange={(e) => { setCountry(e.target.value); setUnsaved(true); }}>
              {/* Add real country options */}
              <MenuItem value="au">Australia</MenuItem>
              <MenuItem value="at">Austria</MenuItem>
              <MenuItem value="be">Belgium</MenuItem>
              <MenuItem value="ca">Canada</MenuItem>
              <MenuItem value="cn">China</MenuItem>
              <MenuItem value="fr">France</MenuItem>
              <MenuItem value="de">Germany</MenuItem>
              <MenuItem value="it">Italy</MenuItem>
              <MenuItem value="jp">Japan</MenuItem>
              <MenuItem value="mx">Mexico</MenuItem>
              <MenuItem value="nl">The Netherlands</MenuItem>
              <MenuItem value="nz">New Zealand</MenuItem>
              <MenuItem value="no">Norway</MenuItem>
              <MenuItem value="kr">South Korea</MenuItem>
              <MenuItem value="es">Spain</MenuItem>
              <MenuItem value="se">Sweden</MenuItem>
              <MenuItem value="ch">Switzerland</MenuItem>
              <MenuItem value="tw">Taiwan</MenuItem>
              <MenuItem value="gb">United Kingdom</MenuItem>
              <MenuItem value="us">United States</MenuItem>
            </Select>
            <Typography variant="h6" style={{ margin: '24px 0 16px' }}>Layouts</Typography>
            {layouts.map((lay, idx) => (
              <Card key={idx} className="card" style={{ marginBottom: '16px', position: 'relative', cursor: editingLayoutIndex === idx ? 'default' : 'pointer' }} onClick={() => {
                if (editingLayoutIndex !== idx) {
                  setEditingLayoutIndex(idx);
                  setLengthKm(lay.length);
                  setLengthMiles((parseFloat(lay.length) / 1.609).toFixed(3));
                  setMapFile(null);
                }
              }}>
                <CardContent>
                  {editingLayoutIndex === idx ? (
                    <div>
                      <Typography variant="h6">{lay.layoutName}</Typography>
                      <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                        <TextField
                          label="Track Length (KM)"
                          value={lengthKm}
                          onChange={(e) => {
                            const km = e.target.value;
                            setLengthKm(km);
                            setLengthMiles(km ? (parseFloat(km) / 1.609).toFixed(3) : '');
                            setUnsaved(true);
                          }}
                          style={{ flex: 1 }}
                        />
                        <TextField
                          label="Track Length (Miles)"
                          value={lengthMiles}
                          onChange={(e) => {
                            const miles = e.target.value;
                            setLengthMiles(miles);
                            setLengthKm(miles ? (parseFloat(miles) * 1.609).toFixed(3) : '');
                            setUnsaved(true);
                          }}
                          style={{ flex: 1 }}
                        />
                      </div>
                      <div style={{ margin: '16px 0' }}>
                        <input type="file" accept="image/*" onChange={(e) => { setMapFile(e.target.files[0]); setUnsaved(true); }} />
                        {(mapFile || lay.map) && <img src={mapFile ? URL.createObjectURL(mapFile) : lay.map} alt="Track Map" style={{ width: '100px', height: '100px', marginTop: '8px', objectFit: 'cover' }} />}
                      </div>
                      <Button onClick={async () => {
                        let newMapUrl = lay.map;
                        if (mapFile) {
                          const compressedMap = await new Promise((resolve) => {
                            new Compressor(mapFile, {
                              quality: 0.8,
                              maxWidth: 1600,
                              success: resolve,
                            });
                          });
                          const mapRef = ref(storage, `circuits/${Date.now()}_map.jpg`);
                          await uploadBytes(mapRef, compressedMap);
                          newMapUrl = await getDownloadURL(mapRef);
                        }
                        const updatedLayouts = [...layouts];
                        updatedLayouts[idx] = { ...lay, length: parseFloat(lengthKm).toFixed(3), map: newMapUrl };
                        setLayouts(updatedLayouts);
                        setEditingLayoutIndex(null);
                        setLengthKm('');
                        setLengthMiles('');
                        setMapFile(null);
                      }}>Save Layout</Button>
                      <Button onClick={() => setEditingLayoutIndex(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography style={{ flex: 1 }}>{lay.layoutName} - {lay.length}km</Typography>
                      {lay.map && <img src={lay.map} alt="Track Map" style={{ width: '50px', height: '50px', objectFit: 'cover', marginLeft: '8px' }} />}
                    </div>
                  )}
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); setShowConfirmDeleteLayout(true); setLayoutToDelete(idx); }}
                    style={{ position: 'absolute', top: 8, right: 8, color: 'red' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
            <TextField fullWidth label="Layout Name" value={layoutName} onChange={(e) => { setLayoutName(e.target.value); setUnsaved(true); }} />
            <div style={{ display: 'flex', gap: '16px' }}>
              <TextField
                label="Track Length (KM)"
                value={lengthKm}
                onChange={(e) => {
                  const km = e.target.value;
                  setLengthKm(km);
                  setLengthMiles(km ? (parseFloat(km) / 1.609).toFixed(3) : '');
                  setUnsaved(true);
                }}
                style={{ flex: 1 }}
              />
              <TextField
                label="Track Length (Miles)"
                value={lengthMiles}
                onChange={(e) => {
                  const miles = e.target.value;
                  setLengthMiles(miles);
                  setLengthKm(miles ? (parseFloat(miles) * 1.609).toFixed(3) : '');
                  setUnsaved(true);
                }}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ margin: '16px 0' }}>
              <input type="file" accept="image/*" onChange={(e) => { setMapFile(e.target.files[0]); setUnsaved(true); }} />
              {mapFile && <img src={URL.createObjectURL(mapFile)} alt="Track Map Preview" style={{ width: '100px', height: '100px', marginTop: '8px', objectFit: 'cover' }} />}
            </div>
            <Button onClick={handleAddLayout} disabled={!layoutName || !lengthKm} style={{ margin: '16px 0' }}>Save Layout</Button>
            <Button onClick={handleAddCircuit} disabled={layouts.length === 0}>Submit Circuit</Button>
            {isEditing && canDeleteCircuit && (
              <Button onClick={handleDeleteCircuit} color="error" style={{ marginLeft: '16px' }}>Delete Circuit</Button>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showConfirmClose} onClose={() => setShowConfirmClose(false)}>
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogContent>
            <Typography>You have unsaved changes. Are you sure you want to close?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmClose(false)}>Cancel</Button>
            <Button onClick={() => { setShowConfirmClose(false); setOpenAddModal(false); setIsEditing(false); resetForm(); }} color="primary">Yes, Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showConfirmDeleteLayout} onClose={() => setShowConfirmDeleteLayout(false)}>
          <DialogTitle>Delete Layout</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this layout?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDeleteLayout(false)}>Cancel</Button>
            <Button onClick={() => { setShowConfirmDeleteLayout(false); setLayouts(layouts.filter((_, i) => i !== layoutToDelete)); setLayoutToDelete(null); }} color="primary">Yes, Delete</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showConfirmDeleteCircuit} onClose={() => setShowConfirmDeleteCircuit(false)}>
          <DialogTitle>Delete Circuit</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this circuit?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDeleteCircuit(false)}>Cancel</Button>
            <Button onClick={confirmDeleteCircuit} color="primary">Yes, Delete</Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default CircuitManager;